'use client';

import Link from 'next/link';

import { config } from '@ps/config/client';
import { bioUrl } from '@ps/functions/bio/bioUrls';
import { cn } from '@ps/ui/utils';

import { useVideoClipsContext } from '../contexts/video-player';
import type { AnyVideo } from '../functions';
import { CommentAction } from './actions/CommentAction';
import { LikeAction } from './actions/LikeAction';
import { MoreAction } from './actions/MoreAction';
import { MuteAction } from './actions/MuteAction';

type VideoActionsProps = {
  video: AnyVideo;
  currentUserId?: string;
  isRealEstateListing?: boolean;
  onOpenComments?: () => void;
  onDelete?: (video: AnyVideo) => void;
  className?: string;
};

export function VideoActions({
  video,
  currentUserId,
  isRealEstateListing,
  onOpenComments,
  onDelete,
  className,
}: VideoActionsProps) {
  const isWelcome = video.extra?.isWelcomeVideo === true;
  const { bioContext } = useVideoClipsContext();

  // The creator's bio home. Built from the player's `bioContext` (custom-domain
  // aware) — a feed belongs to ONE creator, so that context IS this clip's author,
  // and the code stored on the clip can be stale. A real-estate listing player can
  // be embedded off-site, so it keeps its absolute new-tab link instead.
  const creatorUrlCode =
    (video.author as any)?.urlCode ?? (video.author as any)?.username;
  const creatorHomeHref = isRealEstateListing
    ? `https://${config.domain}/${creatorUrlCode}`
    : bioUrl(bioContext).home();
  const externalProps = isRealEstateListing
    ? ({ target: '_blank', rel: 'noopener noreferrer' } as const)
    : {};

  if (isWelcome) {
    return (
      <div
        className={cn('flex flex-col items-center', className)}
        style={{ gap: 8, paddingTop: 15 }}
      >
        <MuteAction />
      </div>
    );
  }

  return (
    <div
      className={cn('flex flex-col items-center', className)}
      style={{ gap: 8, paddingTop: 15 }}
    >
      {/* Avatar */}
      {video.author && (
        <div className="mb-1 flex flex-col items-center">
          {/* Avatar — a link to the creator's bio home. */}
          <Link
            href={creatorHomeHref}
            {...externalProps}
            onClick={event => event.stopPropagation()}
            style={{
              width: 46,
              height: 46,
              borderRadius: '50%',
              overflow: 'hidden',
              border: '1px solid rgba(255, 255, 255, 0.85)',
              padding: 0,
              cursor: 'pointer',
              background: 'none',
              flexShrink: 0,
            }}
          >
            {video.author.avatar ? (
              <img
                src={video.author.avatar}
                alt={(video.author as any).storeFrontName ?? ''}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgb(var(--raise) / 0.2)',
                  color: 'hsl(var(--content-1))',
                  fontWeight: 'bold',
                  fontSize: 16,
                }}
              >
                {((video.author as any).firstName ?? '?')[0]}
              </div>
            )}
          </Link>

          {/* Pink "+" badge — same destination as the avatar it hangs off. */}
          <Link
            href={creatorHomeHref}
            {...externalProps}
            aria-label="Creator profile"
            onClick={event => event.stopPropagation()}
            style={{
              marginTop: -6,
              width: 18,
              height: 18,
              borderRadius: '50%',
              backgroundColor: '#FF1493',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              flexShrink: 0,
            }}
          >
            <svg viewBox="0 0 24 24" width={10} height={10} fill="none">
              <path
                d="M12 4v16M4 12h16"
                stroke="#fff"
                strokeWidth={3}
                strokeLinecap="round"
              />
            </svg>
          </Link>
        </div>
      )}

      <LikeAction
        video={video}
        currentUserId={currentUserId}
        isRealEstateListing={isRealEstateListing}
      />
      <CommentAction video={video} onOpenComments={onOpenComments} />
      <MuteAction />
      <MoreAction
        video={video}
        currentUserId={currentUserId}
        isRealEstateListing={isRealEstateListing}
        onDelete={onDelete}
      />
    </div>
  );
}
